import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Contract,
  JsonRpcProvider,
  formatUnits,
} from "ethers";

import {
  useAppKitAccount,
} from "@reown/appkit/react";


/* =========================================================
   CONFIG
========================================================= */

const BSC_RPC =
  process.env.REACT_APP_BSC_RPC_URL ||
  "https://bsc-dataseed.bnbchain.org";

const CONTRACT_ADDRESS =
  process.env.REACT_APP_KIND_RUSH_CONTRACT_ADDRESS ||
  "0x900ADA53B342ebdF097465cefDD65Ac13Cb928f6";


/* =========================================================
   ABI
========================================================= */

const ABI = [
  "function getActiveCampaignId() view returns (uint256)",

  `function getUserView(
      uint256 campaignId,
      address wallet
    )
    view
    returns (
      (
        uint8 status,
        uint8 reason,
        uint256 firstPurchaseTime,
        uint256 qualificationEnd,
        uint256 qualifyingPurchaseUsd,
        uint256 frozenPurchaseUsd,
        uint256 tradeVolumeUsd,
        uint256 rewardBps,
        uint256 rewardUsd,
        uint256 claimedKind,
        uint256 claimsCompleted,
        uint256 claimCount,
        uint256 tradeCount,
        uint256 nextClaimTime,
        uint256 timeUntilNextClaim,
        uint256 nextClaimUsd,
        uint256 nextClaimKind,
        uint256 currentKindBalance,
        bool qualificationFinished,
        bool claimReady
      ) v
    )`,

  `function getUserTrades(
      uint256 campaignId,
      address wallet,
      uint256 offset,
      uint256 limit
    )
    view
    returns (
      (
        bytes32 tradeId,
        address wallet,
        address pair,
        uint256 kindAmount,
        uint256 usdValue,
        uint256 fullUsdValue,
        uint256 timestamp,
        bool verified
      )[] records,
      uint256 total
    )`,

  "function getCompactKindPrice(uint256 campaignId) view returns (uint256)",
];


/* =========================================================
   CONSTANTS
========================================================= */

const ZERO = 0n;

const STATUS = {
  0: "Not Registered",
  1: "Qualifying",
  2: "Active",
  3: "Completed",
  4: "Disqualified",
};

const REASON = {
  0: "None",
  1: "KIND Sold",
  2: "KIND Sent",
  3: "KIND Received",
  4: "Invalid Trade",
  5: "Unauthorized Contract",
  6: "Admin Emergency",
};


/* =========================================================
   HELPERS
========================================================= */

const usd = (
  value,
  digits = 2
) => {
  try {
    return Number(
      formatUnits(
        value ?? ZERO,
        18
      )
    ).toLocaleString(
      undefined,
      {
        minimumFractionDigits:
          digits,

        maximumFractionDigits:
          digits,
      }
    );
  }
  catch {
    return "0.00";
  }
};


const token = (
  value,
  digits = 2
) => {
  try {
    return Number(
      formatUnits(
        value ?? ZERO,
        18
      )
    ).toLocaleString(
      undefined,
      {
        maximumFractionDigits:
          digits,
      }
    );
  }
  catch {
    return "0";
  }
};


/* =========================================================
   KIND -> USD

   Frontend only calculation.

   KIND amount × current KIND USD price.
========================================================= */

const kindUsdValue = (
  kindAmount,
  kindPriceUsd
) => {
  try {
    const amount =
      Number(
        formatUnits(
          kindAmount ?? ZERO,
          18
        )
      );

    const price =
      Number(
        formatUnits(
          kindPriceUsd ?? ZERO,
          18
        )
      );

    const value =
      amount *
      price;

    if (
      !Number.isFinite(
        value
      )
    ) {
      return "0.00";
    }

    return value.toLocaleString(
      undefined,
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    );
  }
  catch {
    return "0.00";
  }
};


const shortAddress = (
  address
) => {
  if (!address) {
    return "—";
  }

  return `${address.slice(
    0,
    6
  )}...${address.slice(-4)}`;
};


const shortHash = (
  hash
) => {
  if (!hash) {
    return "—";
  }

  return `${hash.slice(
    0,
    10
  )}...${hash.slice(-8)}`;
};


const dateText = (
  timestamp
) => {
  const value =
    Number(
      timestamp || 0
    );

  if (!value) {
    return "—";
  }

  return new Date(
    value * 1000
  ).toLocaleString();
};


const errorMessage = (
  error
) => {
  return (
    error?.shortMessage ||
    error?.reason ||
    error?.message ||
    "Unable to load history"
  );
};


/* =========================================================
   HISTORY PAGE
========================================================= */

export default function History() {
  const {
    address,
    isConnected,
  } = useAppKitAccount();


  /* =======================================================
     STATE
  ======================================================= */

  const [
    campaignId,
    setCampaignId,
  ] = useState(ZERO);

  const [
    userData,
    setUserData,
  ] = useState(null);

  const [
    trades,
    setTrades,
  ] = useState([]);

  const [
    totalTrades,
    setTotalTrades,
  ] = useState(0);

  const [
    kindPriceUsd,
    setKindPriceUsd,
  ] = useState(ZERO);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    message,
    setMessage,
  ] = useState("");


  /* =======================================================
     PROVIDER
  ======================================================= */

  const provider =
    useMemo(
      () => {
        return new JsonRpcProvider(
          BSC_RPC
        );
      },
      []
    );


  const contract =
    useMemo(
      () => {
        return new Contract(
          CONTRACT_ADDRESS,
          ABI,
          provider
        );
      },
      [
        provider,
      ]
    );


  /* =======================================================
     LOAD ALL TRADES
  ======================================================= */

  const loadTrades =
    useCallback(
      async (
        id,
        wallet
      ) => {
        const allTrades = [];

        let offset = 0;

        const limit = 100;

        let total = 1;


        while (
          offset < total
        ) {
          const result =
            await contract
              .getUserTrades(
                id,
                wallet,
                offset,
                limit
              );

          const rows =
            result[0] || [];

          total =
            Number(
              result[1] || 0
            );


          rows.forEach(
            (trade) => {
              allTrades.push({
                tradeId:
                  trade.tradeId,

                wallet:
                  trade.wallet,

                pair:
                  trade.pair,

                kindAmount:
                  trade.kindAmount,

                qualifyingUsd:
                  trade.usdValue,

                fullUsdValue:
                  trade.fullUsdValue,

                timestamp:
                  trade.timestamp,

                verified:
                  Boolean(
                    trade.verified
                  ),
              });
            }
          );


          if (
            rows.length === 0
          ) {
            break;
          }


          offset +=
            rows.length;
        }


        /*
         * Newest trade first.
         */
        allTrades.sort(
          (
            a,
            b
          ) =>
            Number(
              b.timestamp
            ) -
            Number(
              a.timestamp
            )
        );


        setTrades(
          allTrades
        );

        setTotalTrades(
          total
        );
      },
      [
        contract,
      ]
    );


  /* =======================================================
     LOAD PAGE
  ======================================================= */

  const loadData =
    useCallback(
      async (
        showLoader = false
      ) => {
        try {
          if (
            showLoader
          ) {
            setLoading(
              true
            );
          }

          setMessage("");


          const id =
            await contract
              .getActiveCampaignId();


          setCampaignId(
            id
          );


          if (
            id === ZERO
          ) {
            setUserData(
              null
            );

            setTrades(
              []
            );

            setTotalTrades(
              0
            );

            setKindPriceUsd(
              ZERO
            );

            return;
          }


          /* =================================================
             LOAD CURRENT KIND PRICE
          ================================================= */

          try {
            const price =
              await contract
                .getCompactKindPrice(
                  id
                );

            setKindPriceUsd(
              price
            );
          }
          catch (priceError) {
            console.error(
              "KIND price load error:",
              priceError
            );

            setKindPriceUsd(
              ZERO
            );
          }


          if (
            !address
          ) {
            setUserData(
              null
            );

            setTrades(
              []
            );

            setTotalTrades(
              0
            );

            return;
          }


          const user =
            await contract
              .getUserView(
                id,
                address
              );


          setUserData(
            user
          );


          if (
            Number(
              user.status
            ) === 0
          ) {
            setTrades(
              []
            );

            setTotalTrades(
              0
            );

            return;
          }


          await loadTrades(
            id,
            address
          );
        }
        catch (error) {
          console.error(
            "History load error:",
            error
          );

          setMessage(
            errorMessage(
              error
            )
          );
        }
        finally {
          setLoading(
            false
          );
        }
      },
      [
        contract,
        address,
        loadTrades,
      ]
    );


  /* =======================================================
     AUTO REFRESH
  ======================================================= */

  useEffect(
    () => {
      loadData(
        true
      );


      const timer =
        setInterval(
          () => {
            loadData(
              false
            );
          },
          12000
        );


      return () =>
        clearInterval(
          timer
        );
    },
    [
      loadData,
    ]
  );


  /* =======================================================
     TOTALS FROM HISTORY
  ======================================================= */

  const totals =
    useMemo(
      () => {
        let fullVolume =
          ZERO;

        let qualifyingVolume =
          ZERO;

        let kindBought =
          ZERO;

        let verified =
          0;


        trades.forEach(
          (trade) => {
            fullVolume +=
              trade.fullUsdValue ??
              ZERO;

            qualifyingVolume +=
              trade.qualifyingUsd ??
              ZERO;

            kindBought +=
              trade.kindAmount ??
              ZERO;

            if (
              trade.verified
            ) {
              verified += 1;
            }
          }
        );


        return {
          fullVolume,
          qualifyingVolume,
          kindBought,
          verified,
        };
      },
      [
        trades,
      ]
    );


  /* =======================================================
     STATUS
  ======================================================= */

  const status =
    Number(
      userData?.status ?? 0
    );

  const reason =
    Number(
      userData?.reason ?? 0
    );

  const isDisqualified =
    status === 4;


  /* =======================================================
     LOADING
  ======================================================= */

  if (
    loading
  ) {
    return (
      <div style={styles.page}>
        <div style={styles.empty}>
          Loading trade history...
        </div>
      </div>
    );
  }


  /* =======================================================
     NO CAMPAIGN
  ======================================================= */

  if (
    campaignId === ZERO
  ) {
    return (
      <div style={styles.page}>
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>
            ⚡
          </div>

          <h2>
            No Active Campaign
          </h2>

          <p>
            There is currently no
            activated and open campaign.
          </p>
        </div>
      </div>
    );
  }


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* HEADER */}

        <div style={styles.header}>
          <div>
            <div style={styles.eyebrow}>
              KIND CAT TOKEN
            </div>

            <h1 style={styles.title}>
              Trade History
            </h1>

            <div style={styles.subtitle}>
              Kind Bonus Rush
              {" • "}
              Campaign #
              {campaignId.toString()}
            </div>
          </div>


          <button
            style={styles.refreshButton}
            onClick={() =>
              loadData(
                true
              )
            }
          >
            Refresh
          </button>
        </div>


        {/* NO WALLET */}

        {!isConnected ||
        !address ? (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}>
              🔗
            </div>

            <h2>
              Connect Your Wallet
            </h2>

            <p>
              Connect your wallet to
              view your verified KIND
              purchase history.
            </p>
          </div>
        ) : !userData ||
          status === 0 ? (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}>
              📜
            </div>

            <h2>
              No History
            </h2>

            <p>
              This wallet has not
              participated in Campaign #
              {campaignId.toString()}.
            </p>

            <div style={styles.walletBadge}>
              {shortAddress(
                address
              )}
            </div>
          </div>
        ) : (
          <>

            {/* WALLET STATUS */}

            <div style={styles.walletHeader}>

              <div>
                <div style={styles.smallLabel}>
                  WALLET
                </div>

                <div style={styles.walletAddress}>
                  {shortAddress(
                    address
                  )}
                </div>
              </div>


              <div>
                <div style={styles.smallLabel}>
                  STATUS
                </div>

                <div
                  style={{
                    ...styles.status,

                    ...(isDisqualified
                      ? styles.bad
                      : styles.good),
                  }}
                >
                  {
                    STATUS[
                      status
                    ]
                  }
                </div>
              </div>


              {isDisqualified && (
                <div>
                  <div style={styles.smallLabel}>
                    REASON
                  </div>

                  <div style={styles.bad}>
                    {
                      REASON[
                        reason
                      ]
                    }
                  </div>
                </div>
              )}

            </div>


            {/* SUMMARY */}

            <div style={styles.statsGrid}>

              <StatCard
                label="Verified Trades"
                value={
                  totals.verified
                    .toLocaleString()
                }
              />

              <StatCard
                label="Full Trade Volume"
                value={`$${usd(
                  totals.fullVolume
                )}`}
              />

              <StatCard
                label="Qualifying Volume"
                value={`$${usd(
                  totals.qualifyingVolume
                )}`}
              />

              <StatCard
                label="Total KIND Bought"
                value={
                  <>
                    {token(
                      totals.kindBought
                    )}{" "}
                    KIND{" "}

                    <span style={styles.kindUsdSmall}>
                      ($
                      {kindUsdValue(
                        totals.kindBought,
                        kindPriceUsd
                      )}
                      )
                    </span>
                  </>
                }
              />

            </div>


            {/* IMPORTANT INFO */}

            <div style={styles.infoBanner}>
              <div>
                <strong>
                  Full Trade Volume
                </strong>

                <span>
                  {" "}
                  shows the complete
                  verified purchase
                  value.
                </span>
              </div>

              <div style={styles.infoSecond}>
                <strong>
                  Qualifying USD
                </strong>

                <span>
                  {" "}
                  shows only the amount
                  counted toward the
                  $200 reward cap.
                </span>
              </div>
            </div>


            {/* HISTORY */}

            <section style={styles.section}>

              <div style={styles.sectionHeader}>
                <div>
                  <div style={styles.sectionEyebrow}>
                    VERIFIED ACTIVITY
                  </div>

                  <h2 style={styles.sectionTitle}>
                    Purchase History
                  </h2>
                </div>

                <div style={styles.totalBadge}>
                  {totalTrades}{" "}
                  {totalTrades === 1
                    ? "Trade"
                    : "Trades"}
                </div>
              </div>


              {trades.length === 0 ? (
                <div style={styles.noTrades}>
                  No verified purchases
                  have been recorded yet.
                </div>
              ) : (
                <div style={styles.tradeList}>

                  {trades.map(
                    (
                      trade,
                      index
                    ) => {
                      const tradeNumber =
                        trades.length -
                        index;

                      const capDifference =
                        (
                          trade.fullUsdValue ??
                          ZERO
                        ) >
                        (
                          trade.qualifyingUsd ??
                          ZERO
                        );

                      return (
                        <div
                          key={`${trade.tradeId}-${index}`}
                          style={styles.tradeCard}
                        >

                          {/* TOP */}

                          <div style={styles.tradeTop}>

                            <div>
                              <div style={styles.tradeNumber}>
                                Trade #
                                {tradeNumber}
                              </div>

                              <div style={styles.tradeDate}>
                                {dateText(
                                  trade.timestamp
                                )}
                              </div>
                            </div>


                            <div
                              style={
                                trade.verified
                                  ? styles.verifiedBadge
                                  : styles.unverifiedBadge
                              }
                            >
                              {trade.verified
                                ? "✓ Verified"
                                : "Unverified"}
                            </div>

                          </div>


                          {/* VALUES */}

                          <div style={styles.tradeGrid}>

                            <TradeValue
                              label="Full Trade Value"
                              value={`$${usd(
                                trade.fullUsdValue
                              )}`}
                              highlight
                            />

                            <TradeValue
                              label="Qualifying USD"
                              value={`$${usd(
                                trade.qualifyingUsd
                              )}`}
                            />

                            <TradeValue
                              label="KIND Bought"
                              value={
                                <>
                                  {token(
                                    trade.kindAmount
                                  )}{" "}
                                  KIND{" "}

                                  <span style={styles.kindUsdSmall}>
                                    ($
                                    {kindUsdValue(
                                      trade.kindAmount,
                                      kindPriceUsd
                                    )}
                                    )
                                  </span>
                                </>
                              }
                            />

                            <TradeValue
                              label="Pair"
                              value={shortAddress(
                                trade.pair
                              )}
                              mono
                            />

                          </div>


                          {/* CAP NOTICE */}

                          {capDifference && (
                            <div style={styles.capNotice}>
                              The full trade was
                              verified, but only{" "}

                              <strong>
                                $
                                {usd(
                                  trade.qualifyingUsd
                                )}
                              </strong>{" "}

                              counted toward the
                              $200 qualification
                              cap.
                            </div>
                          )}


                          {/* ID */}

                          <div style={styles.tradeFooter}>

                            <div>
                              <div style={styles.hashLabel}>
                                TRADE ID
                              </div>

                              <div style={styles.hash}>
                                {shortHash(
                                  trade.tradeId
                                )}
                              </div>
                            </div>


                            <button
                              type="button"
                              style={styles.copyButton}
                              onClick={() => {
                                if (
                                  navigator
                                    ?.clipboard
                                    ?.writeText
                                ) {
                                  navigator
                                    .clipboard
                                    .writeText(
                                      trade.tradeId
                                    );

                                  setMessage(
                                    "Trade ID copied."
                                  );
                                }
                              }}
                            >
                              Copy ID
                            </button>

                          </div>

                        </div>
                      );
                    }
                  )}

                </div>
              )}

            </section>


            {/* ACCOUNT TOTALS */}

            <section style={styles.section}>

              <div style={styles.sectionHeader}>
                <div>
                  <div style={styles.sectionEyebrow}>
                    ACCOUNT
                  </div>

                  <h2 style={styles.sectionTitle}>
                    Campaign Totals
                  </h2>
                </div>
              </div>


              <div style={styles.accountGrid}>

                <StatCard
                  label="Contract Trade Count"
                  value={Number(
                    userData.tradeCount
                  ).toLocaleString()}
                />

                <StatCard
                  label="Contract Trade Volume"
                  value={`$${usd(
                    userData.tradeVolumeUsd
                  )}`}
                />

                <StatCard
                  label="Qualifying Purchase"
                  value={`$${usd(
                    userData.qualifyingPurchaseUsd
                  )}`}
                />

                <StatCard
                  label="Campaign Status"
                  value={
                    STATUS[
                      status
                    ]
                  }
                  bad={
                    isDisqualified
                  }
                />

              </div>

            </section>

          </>
        )}


        {/* MESSAGE */}

        {message && (
          <div style={styles.message}>
            {message}
          </div>
        )}

      </div>
    </div>
  );
}


/* =========================================================
   COMPONENTS
========================================================= */

function StatCard({
  label,
  value,
  bad = false,
}) {
  return (
    <div style={styles.statCard}>

      <div style={styles.smallLabel}>
        {label}
      </div>

      <div
        style={{
          ...styles.statValue,

          ...(bad
            ? styles.bad
            : {}),
        }}
      >
        {value}
      </div>

    </div>
  );
}


function TradeValue({
  label,
  value,
  highlight = false,
  mono = false,
}) {
  return (
    <div style={styles.tradeValueBox}>

      <div style={styles.tradeLabel}>
        {label}
      </div>

      <div
        style={{
          ...styles.tradeValue,

          ...(highlight
            ? styles.highlight
            : {}),

          ...(mono
            ? styles.mono
            : {}),
        }}
      >
        {value}
      </div>

    </div>
  );
}


/* =========================================================
   STYLES
========================================================= */

const styles = {
  page: {
    minHeight: "100vh",

    background:
      "radial-gradient(circle at top left, rgba(255,204,102,.16), transparent 34%), #080a0f",

    color: "#ffffff",

    padding:
      "28px 18px 70px",

    fontFamily:
      "Inter, Arial, sans-serif",
  },

  container: {
    maxWidth: "1300px",
    margin: "0 auto",
  },

  header: {
    display: "flex",

    justifyContent:
      "space-between",

    alignItems: "center",

    gap: "20px",

    flexWrap: "wrap",

    marginBottom: "25px",
  },

  eyebrow: {
    color: "#ffcc66",

    fontSize: "11px",

    fontWeight: 900,

    letterSpacing: "2px",
  },

  title: {
    margin: "7px 0 0",

    fontSize: "36px",

    fontWeight: 900,

    lineHeight: 1.1,
  },

  subtitle: {
    marginTop: "9px",

    color: "#808593",

    fontSize: "13px",
  },

  refreshButton: {
    border: 0,

    borderRadius: "13px",

    padding: "12px 19px",

    cursor: "pointer",

    color: "#ffffff",

    fontWeight: 800,

    background:
      "linear-gradient(135deg,#ffcc66,#e5ad45)",
  },

  walletHeader: {
    display: "flex",

    alignItems: "center",

    justifyContent:
      "space-between",

    flexWrap: "wrap",

    gap: "25px",

    padding: "20px",

    borderRadius: "18px",

    background:
      "linear-gradient(145deg,#11151d,#0d1016)",

    border:
      "1px solid #222735",

    marginBottom: "13px",
  },

  walletAddress: {
    marginTop: "7px",

    fontFamily: "monospace",

    fontWeight: 800,

    fontSize: "15px",
  },

  status: {
    marginTop: "7px",

    fontWeight: 850,
  },

  good: {
    color: "#65eca0",
  },

  bad: {
    color: "#ff7187",
  },

  smallLabel: {
    color: "#777e8c",

    textTransform:
      "uppercase",

    letterSpacing: "1px",

    fontSize: "10px",

    fontWeight: 850,
  },

  statsGrid: {
    display: "grid",

    gridTemplateColumns:
      "repeat(auto-fit,minmax(200px,1fr))",

    gap: "11px",
  },

  statCard: {
    padding: "19px",

    borderRadius: "17px",

    background:
      "linear-gradient(145deg,#10131a,#0d1016)",

    border:
      "1px solid #202531",
  },

  statValue: {
    marginTop: "9px",

    fontSize: "19px",

    fontWeight: 850,

    overflowWrap: "anywhere",
  },

  kindUsdSmall: {
    color: "#777e8c",

    fontSize: "11px",

    fontWeight: 700,
  },

  infoBanner: {
    marginTop: "13px",

    padding: "15px 18px",

    borderRadius: "14px",

    background:
      "rgba(255,204,102,.08)",

    border:
      "1px solid rgba(255,204,102,.2)",

    color: "#9499a7",

    fontSize: "12px",

    lineHeight: 1.6,
  },

  infoSecond: {
    marginTop: "4px",
  },

  section: {
    marginTop: "17px",

    padding: "22px",

    borderRadius: "22px",

    background:
      "linear-gradient(145deg,rgba(17,20,28,.96),rgba(11,14,20,.96))",

    border:
      "1px solid #202531",
  },

  sectionHeader: {
    display: "flex",

    justifyContent:
      "space-between",

    alignItems: "center",

    gap: "15px",

    flexWrap: "wrap",

    marginBottom: "20px",
  },

  sectionEyebrow: {
    color: "#ffcc66",

    fontSize: "10px",

    fontWeight: 900,

    letterSpacing: "1.5px",
  },

  sectionTitle: {
    margin: "5px 0 0",

    fontSize: "22px",
  },

  totalBadge: {
    padding: "9px 13px",

    borderRadius: "11px",

    background: "#171b25",

    color: "#959bab",

    fontSize: "12px",

    fontWeight: 750,
  },

  tradeList: {
    display: "grid",

    gap: "11px",
  },

  tradeCard: {
    padding: "18px",

    borderRadius: "17px",

    background: "#0b0e14",

    border:
      "1px solid #1e232e",
  },

  tradeTop: {
    display: "flex",

    justifyContent:
      "space-between",

    alignItems:
      "flex-start",

    gap: "15px",

    marginBottom: "17px",
  },

  tradeNumber: {
    fontSize: "15px",

    fontWeight: 850,
  },

  tradeDate: {
    marginTop: "5px",

    color: "#737987",

    fontSize: "11px",
  },

  verifiedBadge: {
    padding: "7px 10px",

    borderRadius: "9px",

    color: "#65eca0",

    background:
      "rgba(101,236,160,.08)",

    border:
      "1px solid rgba(101,236,160,.18)",

    fontSize: "10px",

    fontWeight: 850,
  },

  unverifiedBadge: {
    padding: "7px 10px",

    borderRadius: "9px",

    color: "#ff7187",

    background:
      "rgba(255,113,135,.08)",

    border:
      "1px solid rgba(255,113,135,.18)",

    fontSize: "10px",

    fontWeight: 850,
  },

  tradeGrid: {
    display: "grid",

    gridTemplateColumns:
      "repeat(auto-fit,minmax(180px,1fr))",

    gap: "10px",
  },

  tradeValueBox: {
    padding: "14px",

    borderRadius: "13px",

    background: "#0f1219",

    border:
      "1px solid #1d222c",
  },

  tradeLabel: {
    color: "#6e7583",

    fontSize: "9px",

    textTransform:
      "uppercase",

    letterSpacing: ".8px",

    fontWeight: 800,
  },

  tradeValue: {
    marginTop: "7px",

    fontSize: "14px",

    fontWeight: 800,

    overflowWrap: "anywhere",
  },

  highlight: {
    color: "#ffcc66",

    fontSize: "17px",
  },

  mono: {
    fontFamily: "monospace",
  },

  capNotice: {
    marginTop: "12px",

    padding: "11px 13px",

    borderRadius: "11px",

    color: "#d2b978",

    background:
      "rgba(255,204,102,.07)",

    border:
      "1px solid rgba(255,204,102,.15)",

    fontSize: "11px",
  },

  tradeFooter: {
    display: "flex",

    justifyContent:
      "space-between",

    alignItems: "center",

    gap: "15px",

    flexWrap: "wrap",

    marginTop: "14px",

    paddingTop: "13px",

    borderTop:
      "1px solid #191e27",
  },

  hashLabel: {
    color: "#656b79",

    fontSize: "9px",

    fontWeight: 850,

    letterSpacing: "1px",
  },

  hash: {
    marginTop: "5px",

    fontFamily: "monospace",

    color: "#9a9fab",

    fontSize: "11px",
  },

  copyButton: {
    border:
      "1px solid rgba(255,204,102,.35)",

    background:
      "rgba(255,204,102,.08)",

    color: "#ffcc66",

    borderRadius: "9px",

    padding: "8px 12px",

    cursor: "pointer",

    fontSize: "11px",

    fontWeight: 750,
  },

  accountGrid: {
    display: "grid",

    gridTemplateColumns:
      "repeat(auto-fit,minmax(190px,1fr))",

    gap: "10px",
  },

  noTrades: {
    padding: "45px",

    textAlign: "center",

    color: "#747b89",

    borderRadius: "15px",

    background: "#0b0e14",

    border:
      "1px solid #1d222c",
  },

  empty: {
    maxWidth: "530px",

    margin: "120px auto",

    padding: "45px",

    textAlign: "center",

    borderRadius: "22px",

    background: "#10131a",

    border:
      "1px solid #212632",

    color: "#969cab",
  },

  emptyIcon: {
    fontSize: "35px",

    marginBottom: "10px",
  },

  walletBadge: {
    display: "inline-block",

    marginTop: "12px",

    padding: "9px 13px",

    borderRadius: "10px",

    background: "#171b25",

    color: "#c1c4cd",

    fontFamily: "monospace",
  },

  message: {
    position: "fixed",

    right: "20px",

    bottom: "20px",

    zIndex: 100,

    maxWidth: "400px",

    padding: "14px 17px",

    borderRadius: "13px",

    background: "#161a23",

    border:
      "1px solid #343a49",

    boxShadow:
      "0 15px 50px rgba(0,0,0,.4)",

    fontSize: "13px",
  },
};